import React from 'react';
import { fetchFilters, fetchFilteredData, FilteredDataResponse } from '../../services/filtersApi';

export interface FilterValues {
  unidadeNegocio: string;
  distribuidor: string;
  estado: string;
  municipio: string;
}

interface FilterBarProps {
  onFilterChange?: (filters: FilterValues) => void;
  onDataFiltered?: (data: FilteredDataResponse) => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({ onFilterChange, onDataFiltered }) => {
  const [filters, setFilters] = React.useState<FilterValues>({
    unidadeNegocio: 'Todos',
    distribuidor: 'Todos',
    estado: 'Todos',
    municipio: 'Todos',
  });

  const [buOptions, setBuOptions] = React.useState<string[]>([]);
  const [distributorOptions, setDistributorOptions] = React.useState<string[]>([]);
  const [stateOptions, setStateOptions] = React.useState<string[]>([]);
  const [allCities, setAllCities] = React.useState<Array<{ municipio: string; uf: string }>>([]);
  const [cityOptions, setCityOptions] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    setIsLoading(true);
    try {
      const data = await fetchFilters();
      setBuOptions(data.bu);
      setDistributorOptions(data.distributors);
      setStateOptions(data.states);
      setAllCities(data.cities);
    } catch (error) {
      console.error('Error loading filters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (filters.estado !== 'Todos') {
      const filteredCities = allCities
        .filter(city => city.uf === filters.estado)
        .map(city => city.municipio)
        .sort();
      setCityOptions(filteredCities);
      if (filters.municipio !== 'Todos' && !filteredCities.includes(filters.municipio)) {
        setFilters(prev => ({ ...prev, municipio: 'Todos' }));
      }
    } else {
      setCityOptions([]);
      if (filters.municipio !== 'Todos') {
        setFilters(prev => ({ ...prev, municipio: 'Todos' }));
      }
    }
  }, [filters.estado, allCities]);

  React.useEffect(() => {
    if (filters.unidadeNegocio !== 'Todos' || filters.distribuidor !== 'Todos' ||
        filters.estado !== 'Todos' || filters.municipio !== 'Todos') {
      applyFilters();
    }
  }, [filters]);

  const applyFilters = async () => {
    try {
      const result = await fetchFilteredData({
        unidadeDeNegocio: filters.unidadeNegocio,
        distribuidor: filters.distribuidor,
        uf: filters.estado,
        municipio: filters.municipio
      });

      if (result.success && result.data) {
        onDataFiltered?.(result);
      }
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  return (
    <div className="flex gap-4 p-4 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex flex-col gap-2 flex-1">
        <label className="text-sm font-medium text-gray-700">Unidade de Negócio</label>
        <select
          value={filters.unidadeNegocio}
          onChange={(e) => handleFilterChange('unidadeNegocio', e.target.value)}
          disabled={isLoading}
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="Todos">Todos</option>
          {buOptions.map(bu => (
            <option key={bu} value={bu}>{bu}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2 flex-1">
        <label className="text-sm font-medium text-gray-700">Distribuidor</label>
        <select
          value={filters.distribuidor}
          onChange={(e) => handleFilterChange('distribuidor', e.target.value)}
          disabled={isLoading}
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="Todos">Todos</option>
          {distributorOptions.map(dist => (
            <option key={dist} value={dist}>{dist}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2 flex-1">
        <label className="text-sm font-medium text-gray-700">Estado</label>
        <select
          value={filters.estado}
          onChange={(e) => handleFilterChange('estado', e.target.value)}
          disabled={isLoading}
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="Todos">Todos</option>
          {stateOptions.map(estado => (
            <option key={estado} value={estado}>{estado}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2 flex-1">
        <label className="text-sm font-medium text-gray-700">Município</label>
        <select
          value={filters.municipio}
          onChange={(e) => handleFilterChange('municipio', e.target.value)}
          disabled={isLoading || filters.estado === 'Todos'}
          className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="Todos">Todos</option>
          {cityOptions.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>
    </div>
  );
};
